import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CloseIcon } from '~/assets/icons';
import deviceApi from '~/apis/device';
import { handleApiError, notify } from '~/utils/helper';

type DeviceCommand = {
  action: string;
  name: string;
  parameters: string[];
  description?: string;
};

type DeviceConfigurationDialogProps = {
  open: boolean;
  imei: string;
  onClose: () => void;
};

const DeviceConfigurationDialog = ({ open, imei, onClose }: DeviceConfigurationDialogProps) => {
  const { t } = useTranslation('admin/device');
  const [commands, setCommands] = useState<DeviceCommand[]>([]);
  const [selectedAction, setSelectedAction] = useState('');
  const [values, setValues] = useState<Record<string, string>>({ imei });
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const selectedCommand = commands.find((command) => command.action === selectedAction);

  useEffect(() => {
    if (!open) return;

    const loadCommands = async () => {
      try {
        setIsLoading(true);
        const response = await deviceApi.getAvailableCommands();
        const data = Array.isArray(response.data) ? response.data : response.data?.result;
        const availableCommands = Array.isArray(data) ? data : [];
        setCommands(availableCommands);
        setSelectedAction('');
      } catch (error) {
        setCommands([]);
        setSelectedAction('');
        handleApiError(error);
      } finally {
        setIsLoading(false);
      }
    };

    setValues({ imei });
    loadCommands();
  }, [open, imei]);

  const handleSubmit = async () => {
    if (!selectedCommand) return;

    const missingParameter = selectedCommand.parameters.find((parameter) => !values[parameter]?.trim());
    if (missingParameter) {
      notify('error', t('configurationDialog.requiredParameter', { parameter: missingParameter }));
      return;
    }

    try {
      setIsSubmitting(true);
      const payload = selectedCommand.parameters.reduce<Record<string, string>>((result, parameter) => {
        result[parameter] = values[parameter];
        return result;
      }, {});
      await deviceApi.executeCommand(selectedCommand.action, payload);
      notify('success', t('configurationDialog.sendSuccess'));
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={isSubmitting ? undefined : onClose} fullWidth maxWidth='sm'>
      <DialogTitle className='flex items-center justify-between bg-[#123B6D] text-white'>
        <Box>
          <Typography className='text-white text-xl font-semibold'>
            {t('configurationDialog.title')}
          </Typography>
          <Typography className='text-white/80 text-sm'>{imei}</Typography>
        </Box>
        <IconButton onClick={onClose} disabled={isSubmitting} className='text-white'>
          <CloseIcon className='w-5 h-5' />
        </IconButton>
      </DialogTitle>

      <DialogContent className='pt-6 pb-6'>
        {isLoading ? (
          <Box className='flex justify-center py-8'>
            <CircularProgress />
          </Box>
        ) : commands.length === 0 ? (
          <Typography className='py-6 text-center'>{t('configurationDialog.noCommands')}</Typography>
        ) : (
          <Box className='flex flex-col gap-4 mt-6'>
            <Select
              value={selectedAction}
              displayEmpty
              renderValue={(value) => {
                if (!value) {
                  return (
                    <Typography className='text-[#7B8794]'>
                      {t('configurationDialog.selectCommand')}
                    </Typography>
                  );
                }
                return commands.find((command) => command.action === value)?.name || value;
              }}
              onChange={(event) => {
                setSelectedAction(event.target.value);
                setValues({ imei });
              }}
              fullWidth
            >
              <MenuItem value='' disabled>
                {t('configurationDialog.selectCommand')}
              </MenuItem>
              {commands.map((command) => (
                <MenuItem key={command.action} value={command.action}>
                  {command.name}
                </MenuItem>
              ))}
            </Select>

            {selectedCommand?.description && (
              <Typography className='text-sm text-[#5B6573]'>{selectedCommand.description}</Typography>
            )}

            {selectedCommand && (
              <Box className='flex flex-col gap-4'>
                {selectedCommand.parameters.map((parameter) => (
                  <Box key={parameter}>
                    <Typography className='mb-2 text-sm font-medium text-[#161A1F]'>
                      {parameter}
                      <span className='ml-1 text-red-500'>*</span>
                    </Typography>
                    <TextField
                      value={values[parameter] || ''}
                      disabled={parameter === 'imei'}
                      onChange={(event) =>
                        setValues((current) => ({ ...current, [parameter]: event.target.value }))
                      }
                      fullWidth
                    />
                  </Box>
                ))}
              </Box>
            )}

            <Button
              variant='contained'
              onClick={handleSubmit}
              disabled={isSubmitting || !selectedCommand}
              className='bg-[#123B6D] hover:bg-[#0E2F58]'
            >
              {isSubmitting ? <CircularProgress size={22} color='inherit' /> : t('configurationDialog.send')}
            </Button>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DeviceConfigurationDialog;